import { useEffect, useState } from 'react';
import { discoverByGenre, discoverByProvider, getTopRated, getTrending } from '../../services/tmdb';
import type { Movie, TvShow } from '../../types';
import MediaRow from '../media/MediaRow';

type CatalogType = 'movie' | 'tv';

const PROVIDERS = [
  { id: 8, label: 'Netflix' },
  { id: 337, label: 'Disney+' },
  { id: 1899, label: 'Max' },
  { id: 15, label: 'Hulu' },
  { id: 9, label: 'Prime Video' },
  { id: 350, label: 'Apple TV+' },
] as const;

const GENRES: Record<CatalogType, Array<{ id: number; label: string }>> = {
  movie: [
    { id: 28, label: 'Action' },
    { id: 18, label: 'Drama' },
    { id: 35, label: 'Comedy' },
    { id: 878, label: 'Science Fiction' },
  ],
  tv: [
    { id: 10759, label: 'Action & Adventure' },
    { id: 18, label: 'Drama' },
    { id: 35, label: 'Comedy' },
    { id: 10765, label: 'Sci-Fi & Fantasy' },
  ],
};

function ChoiceChip({ active, children, onClick }: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
        active
          ? 'border-emerald-300 bg-emerald-300 text-black'
          : 'border-white/15 bg-white/[0.06] text-gray-300 hover:border-white/30 hover:bg-white/10 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

export default function HomeCatalogBody() {
  const [type, setType] = useState<CatalogType>('movie');
  const [providerId, setProviderId] = useState(PROVIDERS[0].id as number);
  const [genreId, setGenreId] = useState(GENRES.movie[0].id);
  const [trending, setTrending] = useState<(Movie | TvShow)[]>([]);
  const [topRated, setTopRated] = useState<(Movie | TvShow)[]>([]);
  const [providerTitles, setProviderTitles] = useState<(Movie | TvShow)[]>([]);
  const [genreTitles, setGenreTitles] = useState<(Movie | TvShow)[]>([]);
  const [baseLoading, setBaseLoading] = useState(true);
  const [providerLoading, setProviderLoading] = useState(true);
  const [genreLoading, setGenreLoading] = useState(true);

  const selectedProvider = PROVIDERS.find((provider) => provider.id === providerId) ?? PROVIDERS[0];
  const selectedGenre = GENRES[type].find((genre) => genre.id === genreId) ?? GENRES[type][0];

  useEffect(() => {
    let cancelled = false;
    setBaseLoading(true);

    Promise.all([getTrending(type, 'day'), getTopRated(type)])
      .then(([trendingResponse, topRatedResponse]) => {
        if (cancelled) return;
        setTrending(trendingResponse.results.slice(0, 20));
        setTopRated(topRatedResponse.results.slice(0, 20));
      })
      .catch((error) => console.error('Unable to load the home catalog:', error))
      .finally(() => {
        if (!cancelled) setBaseLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [type]);

  useEffect(() => {
    let cancelled = false;
    setProviderLoading(true);

    discoverByProvider(type, providerId)
      .then((response) => {
        if (!cancelled) setProviderTitles(response.results.slice(0, 20));
      })
      .catch((error) => console.error('Unable to load provider titles:', error))
      .finally(() => {
        if (!cancelled) setProviderLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [providerId, type]);

  useEffect(() => {
    let cancelled = false;
    setGenreLoading(true);

    discoverByGenre(type, [genreId])
      .then((response) => {
        if (!cancelled) setGenreTitles(response.results.slice(0, 20));
      })
      .catch((error) => console.error('Unable to load genre titles:', error))
      .finally(() => {
        if (!cancelled) setGenreLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [genreId, type]);

  const changeType = (nextType: CatalogType) => {
    setType(nextType);
    setGenreId(GENRES[nextType][0].id);
  };

  return (
    <section aria-labelledby="catalog-heading">
      <div className="px-4 sm:px-6 lg:px-12">
        <div className="rounded-3xl border border-white/10 bg-black/30 p-4 shadow-2xl backdrop-blur-md sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-emerald-300/80">
                One catalog, many paths
              </p>
              <h2 id="catalog-heading" className="mt-1 text-2xl font-semibold text-white sm:text-3xl">
                Find what fits tonight
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-400">
                Change the lens without leaving Home. Search stays available above when you already know the title.
              </p>
            </div>

            <div className="inline-flex w-fit rounded-full border border-white/15 bg-black/40 p-1" aria-label="Catalog type">
              <ChoiceChip active={type === 'movie'} onClick={() => changeType('movie')}>Movies</ChoiceChip>
              <ChoiceChip active={type === 'tv'} onClick={() => changeType('tv')}>Series</ChoiceChip>
            </div>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-2">
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500">Only on</p>
              <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
                {PROVIDERS.map((provider) => (
                  <ChoiceChip
                    key={provider.id}
                    active={providerId === provider.id}
                    onClick={() => setProviderId(provider.id)}
                  >
                    {provider.label}
                  </ChoiceChip>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500">Genre</p>
              <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
                {GENRES[type].map((genre) => (
                  <ChoiceChip
                    key={genre.id}
                    active={genreId === genre.id}
                    onClick={() => setGenreId(genre.id)}
                  >
                    {genre.label}
                  </ChoiceChip>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-4 text-[10px] text-gray-600">
            Streaming availability is region-sensitive. Provider data supplied by JustWatch through TMDB.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        <MediaRow title="Trending Today" items={trending} type={type} loading={baseLoading} />
        <MediaRow
          title={`Only on ${selectedProvider.label}`}
          items={providerTitles}
          type={type}
          loading={providerLoading}
        />
        <MediaRow title="Top Rated" items={topRated} type={type} loading={baseLoading} />
        <MediaRow
          title={`${selectedGenre.label} Picks`}
          items={genreTitles}
          type={type}
          loading={genreLoading}
        />
      </div>
    </section>
  );
}
